import type { UseFormSetValue } from 'react-hook-form';
import z from 'zod';
import type {
  FundBusinessFormValues,
  FundBusinessesModel,
} from '../_model/fund-business';

export const MIN_YEAR_BOUNDARY = 3;
export const INVESTMENT_BALANCE_INDEX = 4;

const actualResultsRowKeys = z.enum([
  'new-investment', // 新規出資
  'initial-estimate-investment', // 新規出資(当初見込み)
  'investment-redemption', // 出資償還
  'damage-to-investment', // 出資毀損
  'investment-balance', // 出資残高
]);

const SUBTRACT_KEYS = [
  'investment-redemption', // 出資償還
  'damage-to-investment', // 出資毀損
] as const satisfies ActualResultsRowKeyType[];

type ActualResultsRowKeyType = z.infer<typeof actualResultsRowKeys>;

const actualResultsRowIndexToKeyMap = {
  0: actualResultsRowKeys.Enum['new-investment'],
  1: actualResultsRowKeys.Enum['initial-estimate-investment'],
  2: actualResultsRowKeys.Enum['investment-redemption'],
  3: actualResultsRowKeys.Enum['damage-to-investment'],
  [INVESTMENT_BALANCE_INDEX]: actualResultsRowKeys.Enum['investment-balance'],
} satisfies Record<number, ActualResultsRowKeyType>;

// 0/nullの分岐を行う
export const calculateAmount = (amountList: (number | null)[]) => {
  const allNull = amountList.every((v) => v === null || v === undefined);
  const hasNegativeAmount = amountList.some(
    (v) => typeof v === 'number' && v < 0,
  );
  const amount = amountList.reduce<number | null>((acc, cur) => {
    if (typeof cur === 'number') {
      return (acc ?? 0) + cur;
    }
    return acc;
  }, null);

  return allNull
    ? null
    : amount === 0
      ? hasNegativeAmount
        ? null
        : 0
      : amount;
};

// 出資残高を計算する
export const getCalculatedInvestmentBalance = ({
  minYear,
  performances,
}: {
  minYear: number;
  performances: z.infer<typeof FundBusinessesModel>[number]['performances'];
}) => {
  const projectYear = minYear + MIN_YEAR_BOUNDARY;
  return performances.map((v, i, performanceArray) => {
    const isLast = i === performanceArray.length - 1;
    // 出資残高以外はそのまま返す
    if (!isLast) {
      return v;
    }

    const inputValuesByYear = v.yearAmountInfos.map<{
      year: number;
      defaultCount: number | null;
      amount: number | null;
    }>((yearValue, yearIndex) => {
      const year = minYear + yearIndex;
      const isFeatureYear = year >= projectYear;

      // 初年度は直接入力のため何もしない。
      if (yearIndex === 0) {
        return {
          year,
          defaultCount: yearValue.amountInfo?.count ?? null,
          amount: yearValue.amountInfo?.amount ?? null,
        };
      }

      // 年度ごとの、入力値を出力。出資償還と出資毀損はマイナス値にした上で返す。
      const amountsByRowKey = performanceArray.flatMap<number | null>(
        (v, i) => {
          const key =
            actualResultsRowIndexToKeyMap[
              i as keyof typeof actualResultsRowIndexToKeyMap
            ];

          // 出資残高はBEからのレスポンスではなく計算結果を返すため、２重計算しないようにここで除外する
          if (key === 'investment-balance') {
            return [];
          }

          // 当年度未満は当初見込みは、出資残高の計算には含めない
          if (!isFeatureYear && key === 'initial-estimate-investment') {
            return [];
          }

          const yearAmount =
            v.yearAmountInfos.at(yearIndex)?.amountInfo?.amount;
          const isSubtractKey = SUBTRACT_KEYS.includes(key);

          const amount =
            typeof yearAmount === 'number'
              ? isSubtractKey
                ? yearAmount * -1
                : yearAmount
              : null;

          return [amount];
        },
      );

      return {
        year,
        defaultCount: yearValue.amountInfo?.count ?? null,
        amount: calculateAmount(amountsByRowKey),
      };
    });

    const resYearAmountInfos = inputValuesByYear.map<{
      amountInfo: {
        amount: number | null;
        count: number | null;
      };
    }>(({ defaultCount, amount }, index, arr) => {
      const priorAmountList =
        index === 0
          ? [amount]
          : arr.slice(0, index + 1).flatMap((v) => v.amount);

      return {
        amountInfo: {
          amount: calculateAmount(priorAmountList),
          count: defaultCount,
        },
      };
    });

    return {
      ...v,
      yearAmountInfos: resYearAmountInfos,
    };
  });
};

// formとテーブルの初期値を作成
export const getInitialValueOfCalculatedInvestmentBalance = ({
  fundBusiness,
  fiscalYear,
}: {
  fundBusiness: z.infer<typeof FundBusinessesModel>[number] | undefined;
  fiscalYear: number;
}): FundBusinessFormValues | undefined => {
  const minYear = fiscalYear - MIN_YEAR_BOUNDARY;
  if (!fundBusiness) {
    return undefined;
  }
  const businessForm = fundBusiness.businessForm;
  if (businessForm === 'subsidy' || businessForm === 'others') {
    return fundBusiness;
  }

  return {
    title: fundBusiness.title,
    businessForm: businessForm,
    performances: getCalculatedInvestmentBalance({
      minYear,
      performances: fundBusiness.performances,
    }),
  };
};

// formで出資残高を更新する
export const updateInvestmentBalance = ({
  performances,
  inputValue,
  rowIndex,
  readOnly,
  yearIndex,
  minYear,
  setValue,
}: {
  performances: z.infer<typeof FundBusinessesModel>[number]['performances'];
  inputValue: number | null;
  rowIndex: number;
  readOnly: boolean;
  yearIndex: number;
  minYear: number;
  setValue: UseFormSetValue<FundBusinessFormValues>;
}) => {
  if (readOnly) {
    return;
  }

  const inputAfterPerformances = performances.map((v, i) => {
    if (i === rowIndex) {
      return {
        ...v,
        yearAmountInfos: v.yearAmountInfos.map((v, i) => {
          if (i === yearIndex) {
            return {
              amountInfo: {
                count: v.amountInfo?.count ?? null,
                amount: inputValue,
              },
            };
          }
          return v;
        }),
      };
    }
    return v;
  });

  const investmentBalanceValue = getCalculatedInvestmentBalance({
    minYear,
    performances: inputAfterPerformances,
  }).at(INVESTMENT_BALANCE_INDEX);

  for (const [localYearIndex, { amountInfo }] of (
    investmentBalanceValue?.yearAmountInfos ?? []
  ).entries()) {
    // 初年度はスキップ. また、入力年度より前の年度はスキップ.
    if (localYearIndex === 0 || localYearIndex < yearIndex) {
      continue;
    }

    setValue(
      `performances.${INVESTMENT_BALANCE_INDEX}.yearAmountInfos.${localYearIndex}.amountInfo.amount`,
      amountInfo?.amount ?? null,
    );
  }
};
